import React, { useState, useEffect, useContext } from "react";
import PageBase from "../../components/PageBase";
import { makeStyles, Paper, Tabs, Tab, Box, Container } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";
import HomePageCustomization from "../../components/Settings/HomePageCustomization";
import About from "../../components/Settings/About";
import MaskReferenceGuide from "../../components/MaskReferenceGuide";
import CompanyTimezoneSettings from "../../components/Settings/CompanyTimezoneSettings";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh",
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    flex: 1,
    borderRadius: 16,
    boxShadow: theme.palette.mode === "dark" 
      ? "0px 8px 32px rgba(0, 0, 0, 0.4)" 
      : "0px 8px 32px rgba(0, 0, 0, 0.08)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
    marginLeft: 0,
  },
  tabsContainer: {
    width: "100%",
    minHeight: 56,
    display: "block",
    overflowX: "auto",
    overflowY: "visible",
    position: "relative",
    zIndex: 2,
    marginBottom: 16,
    backgroundColor: theme.palette.mode === "dark" 
      ? "rgba(255, 255, 255, 0.05)" 
      : "rgba(0, 0, 0, 0.02)",
    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
    padding: theme.spacing(0, 2),
    borderRadius: "16px 16px 0 0",
  },
  tabs: {
    minHeight: 48,
    width: "100%",
  },
  tabsFlexContainer: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
  },
  tab: {
    minWidth: 120,
    maxWidth: "none",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    padding: theme.spacing(1.5, 2.5),
    color: theme.palette.text.secondary,
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
    flexShrink: 0,
    minHeight: 48,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === "dark" 
        ? "rgba(255, 255, 255, 0.08)" 
        : "rgba(0, 0, 0, 0.04)",
    },
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },
  tabIndicator: {
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
  contentPaper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    minHeight: "calc(100vh - 300px)",
  },
  container: {
    width: "100%",
    maxWidth: "100%",
    animation: "$fadeIn 0.3s ease-in",
  },
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(10px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const findData = async () => {
      if (!user?.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      } finally {
        setLoading(false);
      }
    };

    findData();
  }, [user?.companyId]);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success(i18n.t("settings.toasts.schedulesUpdated"));
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser && currentUser.super;
  };

  return (
    <PageBase
      className="settings-page app-page-root"
      title={i18n.t("settings.title")}
    >
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, height: "100%" }}>
          <Box className={classes.tabsContainer}>
            <Tabs
              value={tab}
              indicatorColor="primary"
              textColor="primary"
              scrollButtons="auto"
              variant="scrollable"
              onChange={handleTabChange}
              className={classes.tabs}
              classes={{
                indicator: classes.tabIndicator,
                flexContainer: classes.tabsFlexContainer,
              }}
            >
              <Tab 
                className={classes.tab}
                label={i18n.t("settings.tabs.options")} 
                value={"options"} 
              />
              {schedulesEnabled && (
                <Tab 
                  className={classes.tab}
                  label={i18n.t("settings.tabs.schedules") || "Horários"} 
                  value={"schedules"} 
                />
              )}
              {user.profile === "admin" &&
                user.finalizacaoComValorVendaAtiva && (
                  <Tab
                    className={classes.tab}
                    label={i18n.t("settings.tabs.finalization") || "Finalização do Atendimento"}
                    value={"finalizacao"}
                  />
                )}
              {user?.companyId === 1 && (
                <Tab 
                  className={classes.tab}
                  label={i18n.t("settings.tabs.homepage") || "Personalização Home"} 
                  value="homepage" 
                />
              )}
              <Tab 
                className={classes.tab}
                label={i18n.t("settings.tabs.masks") || "Máscaras"} 
                value="masks" 
              />
              <Tab 
                className={classes.tab}
                label={i18n.t("settings.tabs.about") || "Sobre"} 
                value="about" 
              />
            </Tabs>
          </Box>
          <Paper className={classes.contentPaper} elevation={0}>
            {/* Renderização condicional simples - apenas a aba ativa renderiza */}
            {tab === "schedules" && (
              <div className={classes.container}>
                <SchedulesForm
                  loading={loading}
                  onSubmit={handleSubmitSchedules}
                  initialValues={schedules}
                />
              </div>
            )}

            {tab === "finalizacao" && (
              <div className={classes.container}>
                <FinalizacaoAtendimento
                  settings={settings}
                  onSettingsChange={(newSettings) => setSettings(newSettings)}
                />
              </div>
            )}

            {tab === "options" && (
              <div className={classes.container}>
                <CompanyTimezoneSettings
                  company={company}
                  onUpdated={updatedCompany => setCompany(updatedCompany)}
                />
                <Options
                  settings={settings}
                  oldSettings={oldSettings}
                  user={currentUser}
                  scheduleTypeChanged={(value) =>
                    setSchedulesEnabled(value === "company")
                  }
                />
              </div>
            )}
            {tab === "homepage" && user?.companyId === 1 && (
              <div className={classes.container}>
                <HomePageCustomization settings={oldSettings} companyId={user.companyId} />
              </div>
            )}

            {tab === "masks" && (
              <div className={classes.container}>
                <MaskReferenceGuide />
              </div>
            )}

            {tab === "about" && (
              <div className={classes.container}>
                <About />
              </div>
            )}
          </Paper>
        </div>
      )}
    </PageBase>
  );
};

export default SettingsCustom;
